import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSetting } from "@/hooks/useAppSettings";
import { TestimonialStrip } from "@/components/social/TestimonialStrip";

function formatPhone(raw: string): { display: string; tel: string } {
  const digits = raw.replace(/\D/g, "");
  const tel = `tel:+1${digits.replace(/^1/, "")}`;
  // Format as (XXX) XXX-XXXX if 10 digits
  const d = digits.replace(/^1/, "");
  const display =
    d.length === 10
      ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
      : raw;
  return { display, tel };
}

interface Props {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  onBack?: () => void;
  hideBack?: boolean;
}

/**
 * Full-immersion wizard shell — no public-site nav, minimal chrome. Back
 * button is the only escape, and it hands off to browser history so users
 * coming from TC land back on TC.
 */
export function WizardLayout({ children, title, subtitle, onBack, hideBack }: Props) {
  const navigate = useNavigate();
  const phoneRaw = useSetting("company_phone", "289-536-3126");
  const phone = formatPhone(phoneRaw);

  const handleBack = () => {
    if (onBack) return onBack();
    if (window.history.length > 1) navigate(-1);
    else window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-sm">
        <div className="mx-auto flex h-14 max-w-4xl items-center justify-between px-4 sm:px-6">
          {hideBack ? (
            <div className="w-20" aria-hidden />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="gap-1 -ml-2"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
          )}
          <a href="/" className="text-center">
            <span className="font-logo text-base font-bold tracking-wide leading-tight block">
              TRISTANO'S
            </span>
            <span className="text-[9px] font-secondary tracking-[0.25em] text-muted-foreground">
              CATERING
            </span>
          </a>
          <Button variant="ghost" size="sm" className="gap-1 -mr-2" asChild>
            <a href={phone.tel} aria-label="Call us">
              <Phone className="h-4 w-4" />
              <span className="hidden sm:inline">Call</span>
            </a>
          </Button>
        </div>
      </header>

      {/* Body */}
      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-8 sm:py-12">
        {(title || subtitle) && (
          <div className="mb-8 text-center">
            {title && (
              <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="mt-3 text-base text-muted-foreground max-w-xl mx-auto">
                {subtitle}
              </p>
            )}
          </div>
        )}
        {children}
      </main>

      {/* Social proof at the point of conversion, not just the homepage (#256). */}
      <div className="mx-auto max-w-md px-4 sm:px-6">
        <TestimonialStrip limit={1} />
      </div>

      <footer className="border-t border-border/60 mt-12">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-6 text-center text-xs text-muted-foreground">
          Not sure what you need? Call us at{" "}
          <a className="font-medium text-foreground hover:underline" href={phone.tel}>
            {phone.display}
          </a>
        </div>
      </footer>
    </div>
  );
}
